import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { QrCodesService } from '../../../services/qr-codes.service';
import { RestaurantService } from '../../../services/restaurant.service';
import { SettingService } from '../../../services/setting.service';
import { RestaurantQrCodes } from '../../../models/qr-codes';
import { FEATURE_FLAG_KEYS, FeatureFlag, isFeatureActive } from '../../../utils/feature-flag.util';
import { POS_PROVIDER_NAMES } from '../../../utils/pos-provider.util';
import {
  cardCopy,
  downloadCards,
  printCards,
  QrCardSpec,
  renderQrCard,
} from '../../../utils/qr/qr-card';
import { cardCount, cardsByZone, SheetCard, sheetCards } from '../../../utils/qr/qr-sheet';
import {
  countAction,
  countChange,
  CountChange,
  MAX_TOGO_TABLES,
  parseCount,
} from '../../../utils/qr/togo-tables';

/**
 * Operations → QR codes: every code the restaurant prints, on one sheet —
 * the To-Go counter code, the To-Go dining table codes (Table 1..n, made
 * here) and the dine-in table codes (made on the Tables page). The right
 * column previews the picked card and prints or downloads any mix.
 */
@Component({
  selector: 'app-qr-codes',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './qr-codes.component.html',
  styleUrl: './qr-codes.component.css',
})
export class QrCodesComponent implements OnInit, OnDestroy {
  private qrCodesService = inject(QrCodesService);
  private restaurantService = inject(RestaurantService);
  private settingService = inject(SettingService);
  private toastr = inject(ToastrService);
  private subscriptions = new Subscription();

  readonly maxTables = MAX_TOGO_TABLES;

  sheet: RestaurantQrCodes | null = null;
  loading = true;
  /** The sheet request failed with nothing to show (dev 404s until the API lands). */
  loadError: string | null = null;

  restaurantName = '';
  logoUrl: string | null = null;
  /** Order settings → To-Go dining → "Table number required". */
  tableNumberRequired = false;
  /** The Settings page exists only behind the RESTAURANT_SETTINGS flag. */
  canOpenSettings = false;

  /** Every printable card in sheet order, split by section for the template. */
  cards: SheetCard[] = [];
  togoCard: SheetCard | null = null;
  diningCards: SheetCard[] = [];
  dineZones: { zone: string; cards: SheetCard[] }[] = [];
  dineCards: SheetCard[] = [];

  // ----------------------------------------------------- table count control
  countText = '1';
  pending: CountChange | null = null;
  saving = false;

  // ---------------------------------------------------------- preview column
  pickedKey: string | null = null;
  previewUrl: string | null = null;
  previewBusy = false;
  private previewToken = 0;

  selectMode = false;
  selected = new Set<string>();
  /** A print or download is being drawn; every print/download button waits. */
  busy = false;

  ngOnInit() {
    this.subscriptions.add(
      this.restaurantService.currentRestaurantDetails$.subscribe((restaurant: any) => {
        this.restaurantName = (restaurant?.restaurant_name || '').trim();
        this.logoUrl = restaurant?.logo_url || null;
        this.rebuildCards();
      }),
    );
    this.subscriptions.add(
      this.restaurantService.updateRestaurantLogo$.subscribe((restaurant: any) => {
        if (restaurant?.logo_url) {
          this.logoUrl = restaurant.logo_url;
          this.rebuildCards();
        }
      }),
    );
    this.subscriptions.add(
      this.settingService.currentRestaurantSettings$.subscribe((rows: any) => {
        this.tableNumberRequired =
          rows?.find((row: any) => row.setting_type === 'order')?.settings_config?.togo_dine_in
            ?.table_number_required === true;
      }),
    );
    this.subscriptions.add(
      this.restaurantService.restaurantFeatureFlags$.subscribe((flags: FeatureFlag[] | null) => {
        this.canOpenSettings = isFeatureActive(flags, FEATURE_FLAG_KEYS.RESTAURANT_SETTINGS);
      }),
    );
    if (this.settingService.currentRestaurantSettings$.value == null) {
      this.settingService.getSettings().subscribe((response: any) => {
        if (response?.data) this.settingService.currentRestaurantSettings$.next(response.data);
      });
    }
    this.load();
  }

  /** Section pills scroll within the page; an href="#…" would go through the router. */
  jumpTo(id: string) {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.previewToken++;
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
  }

  load() {
    this.loading = true;
    this.loadError = null;
    this.qrCodesService.getQrCodes().subscribe({
      next: (sheet) => {
        this.applySheet(sheet);
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this.loadError =
          error?.status === 404
            ? 'QR codes are not available on this server yet.'
            : error?.error?.error?.message ||
              error?.error?.message ||
              error?.message ||
              'The QR codes could not be loaded.';
      },
    });
  }

  private applySheet(sheet: RestaurantQrCodes) {
    this.sheet = sheet;
    this.countText = String(this.currentCount || 1);
    this.pending = null;
    this.rebuildCards();
  }

  private rebuildCards() {
    this.cards = sheetCards(this.sheet, this.restaurantName || 'Restaurant', this.logoUrl);
    this.togoCard = this.cards.find((c) => c.group === 'togo') ?? null;
    this.diningCards = this.cards.filter((c) => c.group === 'dining');
    this.dineCards = this.cards.filter((c) => c.group === 'dine');
    this.dineZones = cardsByZone(this.cards);
    // Drop selections of codes that no longer exist.
    const keys = new Set(this.cards.map((c) => c.key));
    this.selected.forEach((key) => {
      if (!keys.has(key)) this.selected.delete(key);
    });
    const picked = this.cards.find((c) => c.key === this.pickedKey) ?? this.cards[0] ?? null;
    // The specs are rebuilt (name or logo may have arrived): always redraw.
    this.setPicked(picked, true);
  }

  // -------------------------------------------------------------- summary

  get orderTypes() {
    return (
      this.sheet?.order_types ?? { dine_in: false, togo: false, togo_dine_in: false, online: false }
    );
  }

  get posLabel(): string {
    const pos = this.sheet?.pos_provider || '';
    return POS_PROVIDER_NAMES[pos] ?? (pos ? pos.charAt(0).toUpperCase() + pos.slice(1) : '—');
  }

  get dineZoneCount(): number {
    return this.dineZones.filter((z) => z.zone).length;
  }

  cardCount(n: number) {
    return cardCount(n);
  }

  // ---------------------------------------------------- table count control

  /** Active To-Go dining table codes right now. */
  get currentCount(): number {
    return (this.sheet?.togo_tables ?? []).filter((t) => t.status === 'active').length;
  }

  get parsedCount() {
    return parseCount(this.countText);
  }

  get countValid(): boolean {
    return this.parsedCount.ok;
  }

  get actionLabel(): string {
    const parsed = this.parsedCount;
    return countAction(this.currentCount, parsed.ok ? parsed.value : -1);
  }

  get diningOff(): boolean {
    return !!this.sheet && !this.orderTypes.togo_dine_in;
  }

  stepCount(delta: number) {
    const parsed = this.parsedCount;
    const base = parsed.ok ? parsed.value : this.currentCount || 1;
    this.countText = String(Math.min(MAX_TOGO_TABLES, Math.max(1, base + delta)));
    this.pending = null;
  }

  onCountInput() {
    this.pending = null;
  }

  submitCount() {
    const parsed = this.parsedCount;
    if (!parsed.ok) {
      this.toastr.error(`Enter a number of tables from 1 to ${MAX_TOGO_TABLES}.`, 'Error');
      return;
    }
    this.pending = countChange(this.currentCount, parsed.value);
  }

  cancelCount() {
    this.pending = null;
  }

  applyCount() {
    const parsed = this.parsedCount;
    if (!parsed.ok || this.saving) return;
    this.saving = true;
    this.qrCodesService.setTogoTableCount(parsed.value).subscribe({
      next: (sheet) => {
        this.saving = false;
        this.applySheet(sheet);
        this.toastr.success(
          `To-Go dining codes set for Table 1 – Table ${this.currentCount}.`,
          'Success',
        );
      },
      error: (error) => {
        this.saving = false;
        this.toastr.error(
          error?.error?.error?.message ||
            error?.error?.message ||
            'The table codes could not be saved. Please try again later.',
          'Error',
        );
      },
    });
  }

  // ------------------------------------------------------------ preview

  get pickedCard(): SheetCard | null {
    return this.cards.find((c) => c.key === this.pickedKey) ?? null;
  }

  get pickedCopy() {
    const card = this.pickedCard;
    return card ? cardCopy(card.spec) : null;
  }

  /** "To-Go counter" / "To-Go dining" / "Dine-in" for the picked card. */
  get pickedKindLabel(): string {
    switch (this.pickedCard?.group) {
      case 'togo':
        return 'To-Go counter';
      case 'dining':
        return 'To-Go dining';
      case 'dine':
        return 'Dine-in';
      default:
        return '';
    }
  }

  /** Clicking a tile previews it — or, in select mode, ticks it. */
  onTileClick(card: SheetCard) {
    if (this.selectMode) {
      this.toggleSelected(card);
      return;
    }
    this.setPicked(card);
  }

  isPicked(card: SheetCard): boolean {
    return this.selectMode ? this.selected.has(card.key) : this.pickedKey === card.key;
  }

  private setPicked(card: SheetCard | null, redraw = false) {
    const changed = (card?.key ?? null) !== this.pickedKey || (!this.previewUrl && card);
    this.pickedKey = card?.key ?? null;
    if (changed || redraw) this.renderPreview(card);
  }

  private async renderPreview(card: SheetCard | null) {
    const token = ++this.previewToken;
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
    }
    if (!card) return;
    this.previewBusy = true;
    try {
      const blob = await renderQrCard(card.spec);
      if (token !== this.previewToken) return;
      this.previewUrl = URL.createObjectURL(blob);
    } catch (error: any) {
      if (token === this.previewToken) {
        this.toastr.error(error?.message || 'The card could not be drawn.', 'Error');
      }
    } finally {
      if (token === this.previewToken) this.previewBusy = false;
    }
  }

  // ---------------------------------------------------------- selection

  toggleSelectMode() {
    this.selectMode = !this.selectMode;
    if (!this.selectMode) this.selected.clear();
  }

  toggleSelected(card: SheetCard) {
    if (this.selected.has(card.key)) this.selected.delete(card.key);
    else this.selected.add(card.key);
  }

  get selectedCards(): SheetCard[] {
    return this.cards.filter((c) => this.selected.has(c.key));
  }

  selectAll() {
    this.cards.forEach((c) => this.selected.add(c.key));
  }

  clearSelection() {
    this.selected.clear();
  }

  // ------------------------------------------------- print and download

  private specsOf(cards: SheetCard[]): QrCardSpec[] {
    return cards.map((c) => c.spec);
  }

  async print(cards: SheetCard[], what = 'QR codes') {
    if (this.busy || cards.length === 0) return;
    this.busy = true;
    try {
      const opened = await printCards(this.specsOf(cards), `${this.restaurantName} ${what}`.trim());
      if (!opened) {
        this.toastr.warning('Allow pop-ups for this site to print the cards.', 'Pop-up blocked');
      }
    } catch (error: any) {
      this.toastr.error(error?.message || 'The cards could not be printed.', 'Error');
    } finally {
      this.busy = false;
    }
  }

  async download(cards: SheetCard[], what = 'QR codes') {
    if (this.busy || cards.length === 0) return;
    this.busy = true;
    try {
      await downloadCards(this.specsOf(cards), `${this.restaurantName} ${what}`.trim());
    } catch (error: any) {
      this.toastr.error(error?.message || 'The cards could not be downloaded.', 'Error');
    } finally {
      this.busy = false;
    }
  }

  printSheet() {
    return this.print(this.cards, 'QR codes');
  }

  downloadSheet() {
    return this.download(this.cards, 'QR codes');
  }

  printPicked() {
    const card = this.pickedCard;
    return card ? this.print([card], cardCopy(card.spec).code) : undefined;
  }

  downloadPicked() {
    const card = this.pickedCard;
    return card ? this.download([card], cardCopy(card.spec).code) : undefined;
  }

  printSelected() {
    return this.print(this.selectedCards, 'selected QR codes');
  }

  downloadSelected() {
    return this.download(this.selectedCards, 'selected QR codes');
  }
}
